import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useLocale from './useLocale';
import { useSupportedLanguageCodes } from './useSupportedLanguages';

/**
 * Custom hook to detect and apply language from URL parameters
 * Supports URLs like: /dashboard?lang=fr or /jobs?lang=de
 * 
 * This hook:
 * - Runs on initial page load and route changes
 * - Checks for 'lang' URL parameter
 * - Validates against dynamically loaded supported languages
 * - Updates the browser and session locale if valid
 * - Integrates with existing localStorage system
 */
const useUrlLanguageDetection = () => {
    const location = useLocation();
    const { setBrowserLocale } = useLocale();
    const supportedLanguageCodes = useSupportedLanguageCodes();

    useEffect(() => {
        // Skip if supported languages haven't loaded yet
        if (supportedLanguageCodes.length === 0) {
            return;
        }

        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const langParam = urlParams.get('lang')?.toLowerCase();

        // Check if the language parameter exists and is supported
        if (langParam && supportedLanguageCodes.includes(langParam)) {
            // Use the existing locale system to update language
            setBrowserLocale(langParam);

            // Remove the lang parameter from URL to clean it up
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('lang');
            window.history.replaceState({}, '', newUrl.toString());
        }
    }, [location.search, setBrowserLocale, supportedLanguageCodes]);
};

export default useUrlLanguageDetection; 